/**
 * The donation form's behaviour on the page.
 *
 * The form is rendered by render.php and every message comes from there, on
 * data attributes or in the server's answer, so nothing here needs
 * translating. This only:
 *  - selects "other amount" when its field is typed in,
 *  - shows the name and email fields while "donate with my name" is ticked,
 *  - sends the form to be checked and shows Paytrail's payment methods in
 *    the block's place of the form. Nothing is saved yet: only picking a
 *    method starts the donation, and the donor goes straight to their bank
 *    or MobilePay, without Paytrail's own page. Only if there are no methods
 *    to show, or the chosen one has no form, does it go to that page.
 */

/**
 * An element, its class and text.
 *
 * @param {string} tag       Tag name.
 * @param {string} className Class.
 * @param {string} text      Text content.
 * @return {HTMLElement} The element.
 */
function element(tag, className, text = '') {
	const el = document.createElement(tag);
	el.className = className;
	el.textContent = text;

	return el;
}

/**
 * One payment method, drawn as its logo: a button that starts the donation.
 *
 * @param {Object}   provider A method from the server's answer.
 * @param {Function} choose   Called with the method when it is picked.
 * @return {HTMLElement} The method.
 */
function methodButton(provider, choose) {
	const method = element('div', 'ptd-method');
	const button = element('button', 'ptd-method__button');
	button.type = 'button';

	if (provider.svg) {
		const logo = document.createElement('img');
		logo.src = provider.svg;
		logo.alt = provider.name;
		logo.className = 'ptd-method__logo';
		button.appendChild(logo);
	} else {
		button.textContent = provider.name;
	}

	button.addEventListener('click', () => choose(provider));
	method.appendChild(button);

	return method;
}

/**
 * Posts Paytrail's form for the chosen method: the browser leaves for the
 * bank or service.
 *
 * @param {Object} provider The method's form from the server's answer.
 */
function postToProvider(provider) {
	const form = document.createElement('form');
	form.method = 'POST';
	form.action = provider.url;
	form.hidden = true;

	provider.parameters.forEach((parameter) => {
		const input = document.createElement('input');
		input.type = 'hidden';
		input.name = parameter.name;
		input.value = parameter.value;
		form.appendChild(input);
	});

	document.body.appendChild(form);
	form.submit();
}

/**
 * Starts the donation: saves it, opens its payment, and sends the donor on.
 *
 * @param {HTMLFormElement} form     The donation form.
 * @param {Object}          payload  What the form sends.
 * @param {Object|null}     provider The chosen method, null for Paytrail's own page.
 * @return {Promise<string>} An error message when the donor stays here, '' when they go.
 */
async function startDonation(form, payload, provider) {
	try {
		const response = await window.fetch(form.action, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({
				...payload,
				method: provider?.id || '',
				method_name: provider?.name || '',
			}),
		});
		const body = await response.json().catch(() => ({}));

		if (response.ok && body.provider?.url) {
			// Noted now: the bank's form leaves the page on its own.
			rememberDialog(form);
			postToProvider(body.provider);
			return '';
		}

		if (response.ok && body.href) {
			rememberDialog(form);
			window.location.assign(body.href);
			return '';
		}

		return body.message || form.dataset.errorNetwork;
	} catch {
		return form.dataset.errorNetwork;
	}
}

/**
 * The donation as the page around the form may want to know it: the amount
 * in euros, and the purpose by ID and by name.
 *
 * @param {HTMLFormElement} form    The donation form.
 * @param {Object}          payload What the form sends.
 * @return {Object} The donation.
 */
function donationDetail(form, payload) {
	const field = form.querySelector('[name="purpose"]');
	const name =
		field && field.tagName === 'SELECT'
			? field.selectedOptions[0]?.textContent || ''
			: field?.dataset.name || '';

	return {
		amount: Number(String(payload.amount).replace(',', '.')) || 0,
		currency: 'EUR',
		purposeId: Number(payload.purpose) || 0,
		purpose: name.trim(),
	};
}

/**
 * Tells the page a step was taken, as a paytrail-donations:{step} event on
 * the form: "begin" when the payment methods show, "payment" when one is
 * picked. For a site's analytics; the plugin itself does nothing with them.
 *
 * @param {HTMLFormElement} form   The donation form.
 * @param {string}          step   begin or payment.
 * @param {Object}          detail The donation, see donationDetail().
 */
function announce(form, step, detail) {
	form.dispatchEvent(
		new window.CustomEvent(`paytrail-donations:${step}`, {
			bubbles: true,
			detail,
		})
	);
}

/**
 * The payment step, in the form's place: the amount, the methods by group,
 * Paytrail's terms, and a way back to the form.
 *
 * @param {HTMLFormElement} form    The donation form.
 * @param {Object}          answer  The server's answer.
 * @param {string}          amount  The amount as the donor gave it.
 * @param {Object}          payload What the form sends.
 */
function showMethods(form, answer, amount, payload) {
	const step = element('div', 'ptd-donation__methods');
	step.tabIndex = -1;

	const error = element('p', 'ptd-donation__error');
	error.setAttribute('role', 'alert');
	error.hidden = true;

	// One at a time: every button waits while a donation is being started.
	const choose = async (provider) => {
		const buttons = step.querySelectorAll('button');
		buttons.forEach((button) => (button.disabled = true));
		error.hidden = true;

		announce(form, 'payment', {
			...donationDetail(form, payload),
			method: provider.id,
			methodName: provider.name,
		});

		const message = await startDonation(form, payload, provider);

		if (message) {
			error.textContent = message;
			error.hidden = false;
			buttons.forEach((button) => (button.disabled = false));
		}
	};

	step.appendChild(
		element('p', 'ptd-donation__methods-title', form.dataset.methodsTitle)
	);
	step.appendChild(
		element(
			'p',
			'ptd-donation__methods-summary',
			form.dataset.methodsSummary.replace(
				'%s',
				`${amount.replace('.', ',')} €`
			)
		)
	);

	// Paytrail's groups in its order; a method in no listed group still shows.
	const groups = [...answer.groups, { id: '', name: '' }];

	groups.forEach((group) => {
		const known = answer.groups.map((g) => g.id);
		const providers = answer.providers.filter((provider) =>
			group.id
				? provider.group === group.id
				: !known.includes(provider.group)
		);

		if (!providers.length) {
			return;
		}

		const section = element('div', 'ptd-donation__method-group');

		if (group.name) {
			section.appendChild(
				element('p', 'ptd-donation__method-group-title', group.name)
			);
		}

		const grid = element('div', 'ptd-donation__method-grid');
		providers.forEach((provider) =>
			grid.appendChild(methodButton(provider, choose))
		);
		section.appendChild(grid);
		step.appendChild(section);
	});

	if (answer.terms) {
		// Reduced to text and one link on the server.
		const terms = element('p', 'ptd-donation__terms');
		terms.innerHTML = answer.terms;
		terms.querySelectorAll('a').forEach((link) => {
			link.target = '_blank';
			link.rel = 'noopener noreferrer';
		});
		step.appendChild(terms);
	}

	step.appendChild(error);

	const back = element(
		'button',
		'ptd-donation__back',
		form.dataset.methodsBack
	);
	back.type = 'button';
	back.addEventListener('click', () => {
		step.remove();
		form.hidden = false;
		form.querySelector('.ptd-donation__submit').disabled = false;
		form.querySelector('input, select')?.focus();
	});
	step.appendChild(back);

	form.hidden = true;
	form.after(step);
	step.focus();
}

function initForm(form) {
	const named = form.querySelector('input[name="named"]');
	const names = named
		? form.querySelector(`#${named.getAttribute('aria-controls')}`)
		: null;
	const customRadio = form.querySelector(
		'input[name="amount"][value="custom"]'
	);
	const customInput = form.querySelector('input[name="custom_amount"]');
	const error = form.querySelector('.ptd-donation__error');
	const submit = form.querySelector('.ptd-donation__submit');

	const showError = (message) => {
		error.textContent = message;
		error.hidden = !message;
	};

	if (named && names) {
		named.addEventListener('change', () => {
			names.hidden = !named.checked;
			named.setAttribute('aria-expanded', String(named.checked));

			// An error showing now is about the name or email fields that
			// just came or went; it no longer applies.
			showError('');
		});
	}

	if (customRadio && customInput) {
		const pickCustom = () => {
			customRadio.checked = true;
		};

		customInput.addEventListener('focus', pickCustom);
		customInput.addEventListener('input', pickCustom);
		customRadio.addEventListener('change', () => customInput.focus());
	}

	form.addEventListener('submit', async (event) => {
		event.preventDefault();
		showError('');

		const data = new window.FormData(form);
		let amount = data.get('amount');

		if (amount === 'custom') {
			amount = String(data.get('custom_amount') || '').trim();
		}

		if (!amount) {
			showError(form.dataset.errorAmount);
			return;
		}

		// Anonymous unless ticked: a name or email typed and then unticked
		// is not sent.
		const isNamed = data.get('named') === '1';

		const payload = {
			amount,
			purpose: data.get('purpose') || '',
			named: isNamed,
			first_name: isNamed ? data.get('first_name') || '' : '',
			last_name: isNamed ? data.get('last_name') || '' : '',
			email: isNamed ? data.get('email') || '' : '',
			website: data.get('website') || '',
			page_id: Number(data.get('page_id')) || 0,
			return_url: data.get('return_url') || '',
		};

		submit.disabled = true;

		// Checked and the methods listed; nothing is saved until one is
		// picked.
		try {
			const response = await window.fetch(form.dataset.methodsUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload),
			});
			const body = await response.json().catch(() => ({}));

			if (
				response.ok &&
				Array.isArray(body.providers) &&
				body.providers.length
			) {
				announce(form, 'begin', donationDetail(form, payload));
				showMethods(form, body, amount, payload);
				return;
			}

			if (response.ok) {
				// No methods to draw: straight to Paytrail's own page.
				announce(form, 'begin', donationDetail(form, payload));
				const message = await startDonation(form, payload, null);

				if (!message) {
					return;
				}

				showError(message);
			} else {
				showError(body.message || form.dataset.errorNetwork);
			}
		} catch {
			showError(form.dataset.errorNetwork);
		}

		submit.disabled = false;
	});
}

const ORIGIN_KEY = 'ptd-donation-dialog';

/**
 * Notes the dialog a form is in before the donor leaves for the bank.
 *
 * A page can carry several donation forms in closed dialogs -- a theme's
 * donation popups -- and all of them show the receipt on the way back. The
 * dialog's id is what tells which one the donor was looking at.
 *
 * @param {HTMLFormElement} form The form being sent.
 */
function rememberDialog(form) {
	try {
		window.sessionStorage.setItem(
			ORIGIN_KEY,
			form.closest('dialog[id]')?.id || ''
		);
	} catch {
		// Storage off: the first receipt on the page is shown instead.
	}
}

/**
 * The dialog noted by rememberDialog(), read once.
 *
 * @return {string} Its id, or '' when there was none.
 */
function takeDialog() {
	try {
		const id = window.sessionStorage.getItem(ORIGIN_KEY) || '';
		window.sessionStorage.removeItem(ORIGIN_KEY);
		return id;
	} catch {
		return '';
	}
}

document.querySelectorAll('.ptd-donation__form').forEach(initForm);

// Back from the bank with the browser's back button, the page comes from
// its cache as it was left: the methods waiting, every button off.
window.addEventListener('pageshow', (event) => {
	if (event.persisted) {
		document
			.querySelectorAll('.ptd-donation__methods button')
			.forEach((button) => (button.disabled = false));
	}
});

/**
 * Closes the receipt and brings back the form for a new donation.
 *
 * Every donation form on the page showed the receipt -- the page's own and
 * those in donation popups -- so all of them go back. The receipt's address
 * goes too, or a reload would show it again.
 */
document.addEventListener('click', (event) => {
	const close = event.target.closest('.ptd-receipt__close');

	if (!close) {
		return;
	}

	const block = close.closest('.ptd-donation');

	document.querySelectorAll('.ptd-receipt').forEach((receipt) => {
		receipt.remove();
	});
	document.querySelectorAll('.ptd-donation__form[hidden]').forEach((form) => {
		form.hidden = false;
	});

	const url = new URL(window.location.href);
	url.searchParams.delete('donation');
	url.searchParams.delete('receipt');
	window.history.replaceState(null, '', url);

	// Focus to the amount the form starts on, rather than the purpose: on iOS
	// focusing a select opens its picker.
	const form = block?.querySelector('.ptd-donation__form');

	(
		form?.querySelector('input[name="amount"]:checked') ??
		form?.querySelector('input')
	)?.focus();
});

// Back from Paytrail: bring the receipt or the message into view and to the
// reader. A form in a popup (a <dialog>, closed on page load) opens its
// popup first, or the receipt would be there but out of sight. Every form on
// the page shows it, so the one the donor left from is preferred.
const NOTICE = '.ptd-receipt, .ptd-donation__notice';
const origin = takeDialog();
const notice =
	(origin && document.getElementById(origin)?.querySelector(NOTICE)) ||
	document.querySelector(NOTICE);

if (notice) {
	const dialog = notice.closest('dialog');

	if (dialog && !dialog.open && typeof dialog.showModal === 'function') {
		dialog.showModal();
	}

	notice.focus();
}
